import { PHONE_QUERY, REDUCED_MOTION_QUERY, matchesNow } from './useMatchMedia'
import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { SWIPE, swipeMoves, swipeOffset, swipeProgress } from 'utils/socialCampaign/postEditor'

import styles from './CampaignPostEditor.module.sass'

export type SwipeDirection = 'next' | 'prev'

interface Options {
  /** An element inside the editor's dialog: the swipe binds to the dialog around it */
  boxRef: RefObject<HTMLElement>
  /** A swipe walks the posts: on a phone, reviewing or editing a post (not while one is written by hand) */
  enabled: boolean
  previousId: string | null
  nextId: string | null
  /** The pane on show: it moves with the post */
  pane: 'edit' | 'preview'
  /** Where the post shown is: a new one plays its entrance */
  postId: string | null
  /** A swipe let go far enough towards a post: the editor walks there (`slide`), or springs back (`reset`) */
  onSwipe: (to: string, direction: SwipeDirection) => void
}

const SLIDE_MS = 180
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'

/** The editor's dialog, around the element the editor holds a ref on (the Modal takes none) */
const dialogOf = (ref: RefObject<HTMLElement>) => ref.current?.closest<HTMLElement>('[role="dialog"]') ?? null

/**
 * What moves is the post (prototype `swParts`): its title and description, the Edit / Preview tabs (not the bar's
 * baseline) and the open pane, each marked by the editor.
 */
const partsOf = (box: HTMLElement, pane: 'edit' | 'preview') => [
  ...Array.from(box.querySelectorAll<HTMLElement>('[data-swipe-part]')),
  ...Array.from(box.querySelectorAll<HTMLElement>('[data-swipe-tabs] [role="tab"]')),
  box.querySelector<HTMLElement>(`[data-swipe-pane="${pane}"]`),
].filter((part): part is HTMLElement => part !== null)

/** How a part moves once let go: at once with reduced motion */
const settling = () => matchesNow(REDUCED_MOTION_QUERY) ? 'none' : `transform ${SLIDE_MS}ms ${EASE}, opacity ${SLIDE_MS}ms ${EASE}`

const place = (parts: HTMLElement[], transform: string, opacity: string) => parts.forEach((part) => {
  part.style.transform = transform
  part.style.opacity = opacity
})

const hintsOf = (box: HTMLElement) => Array.from(box.querySelectorAll<HTMLElement>('[data-swipe-hint]'))

/** Short of the threshold, or a walk refused: the post comes back in place, the hints go */
const springBack = (box: HTMLElement, parts: HTMLElement[]) => {
  parts.forEach((part) => { part.style.transition = settling() })
  place(parts, '', '')
  hintsOf(box).forEach((element) => {
    element.style.opacity = '0'
    element.style.transform = ''
    delete element.dataset.armed
  })
}

/**
 * Where a swipe may not start: a field being typed in, a select or an open list, or a part of the post that opts out
 * (`data-swipe-ignore`, e.g. a strip that scrolls sideways).
 */
const keepsTheFinger = (target: HTMLElement) => {
  const field = target.closest('input:not([type="checkbox"]):not([type="radio"]), textarea, [contenteditable="true"]')
  return (!!field && field === document.activeElement) || !!target.closest('select, [role="listbox"], [role="menu"], [data-swipe-ignore]')
}

/**
 * The swipe between posts on a phone (v2 decision 20, prototype v3 `touchstart` / `touchmove` / `swEnd`): the post
 * follows the finger and fades, a round hint slides in on the side the next post comes from and fills once letting go
 * would move (a third of the width, or a flick); short of it the post springs back. At either end it gives a fifth of
 * the drag. A swipe may start anywhere on the post, the click it would end in is cancelled; a second finger cancels
 * it. `slide` plays the same move for the pager's arrows and "Approve & next"; a desktop, or reduced motion, moves at
 * once. The transforms are written straight on the elements, as the prototype does: a re-render per finger move would
 * be too slow.
 */
export function useEditorSwipe({ boxRef, enabled, previousId, nextId, pane, postId, onSwipe }: Options) {
  const latest = useRef({ enabled, previousId, nextId, pane, onSwipe })
  latest.current = { enabled, previousId, nextId, pane, onSwipe }
  const entering = useRef<'left' | 'right' | null>(null)
  const sliding = useRef(false)
  // a swipe let go far enough: no new one until the editor walked or sprang back
  const handedOver = useRef(false)
  // a slide on its way: an editor closed meanwhile must not walk (its address would reopen it)
  const slideTimer = useRef<number | null>(null)
  useEffect(() => () => {
    if (slideTimer.current !== null) window.clearTimeout(slideTimer.current)
  }, [])

  // a post reached by a swipe or a slide comes in from the side it was on; any other post shows in place
  useLayoutEffect(() => {
    const box = dialogOf(boxRef)
    if (!box) return
    const parts = partsOf(box, latest.current.pane)
    parts.forEach((part) => {
      part.style.transition = ''
      part.style.transform = ''
      part.style.opacity = ''
      if (entering.current) part.dataset.enter = entering.current
      else delete part.dataset.enter
    })
    hintsOf(box).forEach((hint) => hint.remove())
    entering.current = null
    sliding.current = false
    handedOver.current = false
  }, [boxRef, postId])

  /** A walk refused (its save failed) after a swipe: the post comes back in place */
  const reset = useCallback(() => {
    const box = dialogOf(boxRef)
    sliding.current = false
    handedOver.current = false
    if (box) springBack(box, partsOf(box, latest.current.pane))
  }, [boxRef])

  /**
   * The pager's arrows, "Approve & next" and a swipe let go far enough: the post slides out towards where it goes
   * (from where the finger left it), then `then` shows the next, which slides in.
   */
  const slide = useCallback((direction: SwipeDirection, then: () => void) => {
    const box = dialogOf(boxRef)
    if (sliding.current) return
    handedOver.current = false
    if (!box || !matchesNow(PHONE_QUERY) || matchesNow(REDUCED_MOTION_QUERY)) {
      then()
      return
    }
    sliding.current = true
    const parts = partsOf(box, latest.current.pane)
    parts.forEach((part) => {
      part.style.transition = settling()
      delete part.dataset.enter
    })
    // settle the current place, so the move animates from it
    void box.offsetWidth
    place(parts, `translateX(${direction === 'next' ? -box.clientWidth : box.clientWidth}px)`, '0')
    slideTimer.current = window.setTimeout(() => {
      slideTimer.current = null
      entering.current = direction === 'next' ? 'right' : 'left'
      then()
    }, SLIDE_MS)
  }, [boxRef])

  useEffect(() => {
    const box = dialogOf(boxRef)
    if (!box) return

    let gesture: {
      x: number, y: number, dx: number, lock: 'x' | 'y' | null, prev: string | null, next: string | null,
      parts: HTMLElement[], width: number, velocity: number, lastX: number, lastAt: number,
    } | null = null
    let blockClickUntil = 0

    const hint = (side: SwipeDirection) => {
      let element = box.querySelector<HTMLElement>(`[data-swipe-hint][data-side="${side}"]`)
      if (!element) {
        element = document.createElement('span')
        element.className = styles.swipeHint
        element.dataset.swipeHint = ''
        element.dataset.side = side
        element.setAttribute('aria-hidden', 'true')
        element.innerHTML = side === 'prev'
          ? '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>'
          : '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>'
        box.querySelector('[data-swipe-body]')?.appendChild(element)
      }
      return element
    }

    const onStart = (event: TouchEvent) => {
      gesture = null
      const { enabled: on, previousId: prev, nextId: next, pane: open } = latest.current
      if (!on || sliding.current || handedOver.current || event.touches.length !== 1 || !matchesNow(PHONE_QUERY)) return
      const target = event.target as HTMLElement
      if (!target.closest('[data-swipe-body], [data-swipe-part]') || keepsTheFinger(target)) return
      const touch = event.touches[0]
      gesture = {
        x: touch.clientX, y: touch.clientY, dx: 0, lock: null, prev, next,
        parts: partsOf(box, open), width: box.clientWidth, velocity: 0, lastX: touch.clientX, lastAt: Date.now(),
      }
    }

    const end = () => {
      if (!gesture || gesture.lock !== 'x' || !gesture.parts.length) {
        gesture = null
        return
      }
      const done = gesture
      gesture = null
      delete box.dataset.swiping
      // the button the swipe started on is not pressed
      blockClickUntil = Date.now() + 500
      const to = done.dx < 0 ? done.next : done.prev
      done.parts.forEach((part) => {
        part.style.transition = settling()
      })
      const moves = swipeMoves({ offset: done.dx, width: done.width, velocity: done.velocity, idleMs: Date.now() - done.lastAt, hasTarget: !!to })
      if (to && moves) {
        // the editor saves the post, then slides it out (`slide`) or springs it back (`reset`) when the save is refused
        hintsOf(box).forEach((element) => { element.style.opacity = '0' })
        handedOver.current = true
        latest.current.onSwipe(to, done.dx < 0 ? 'next' : 'prev')
      } else {
        springBack(box, done.parts)
      }
    }

    const cancel = () => {
      if (gesture && gesture.lock === 'x') {
        gesture.dx = 0
        gesture.next = null
        gesture.prev = null
        end()
      } else {
        gesture = null
      }
    }

    const onMove = (event: TouchEvent) => {
      if (!gesture) return
      // a second finger: not a swipe
      if (event.touches.length > 1) {
        cancel()
        return
      }
      const touch = event.touches[0]
      const dx = touch.clientX - gesture.x
      const dy = touch.clientY - gesture.y
      if (!gesture.lock) {
        if (Math.abs(dx) < SWIPE.lockDistance && Math.abs(dy) < SWIPE.lockDistance) return
        gesture.lock = Math.abs(dx) > Math.abs(dy) * SWIPE.lockRatio ? 'x' : 'y'
        if (gesture.lock === 'x') {
          box.dataset.swiping = ''
          gesture.parts.forEach((part) => {
            part.style.transition = 'none'
            delete part.dataset.enter
          })
        }
      }
      if (gesture.lock !== 'x' || !gesture.parts.length) return
      event.preventDefault()
      const now = Date.now()
      if (now > gesture.lastAt) {
        gesture.velocity = (touch.clientX - gesture.lastX) / (now - gesture.lastAt)
        gesture.lastX = touch.clientX
        gesture.lastAt = now
      }
      const to = dx < 0 ? gesture.next : gesture.prev
      gesture.dx = swipeOffset(dx, !!to)
      const progress = swipeProgress(gesture.dx, gesture.width)
      place(gesture.parts, `translateX(${gesture.dx}px)`, String(1 - progress * 0.35))
      const side: SwipeDirection = dx < 0 ? 'next' : 'prev'
      const other = box.querySelector<HTMLElement>(`[data-swipe-hint][data-side="${side === 'next' ? 'prev' : 'next'}"]`)
      if (other) other.style.opacity = '0'
      const shown = hint(side)
      if (to) {
        shown.style.opacity = String(progress)
        shown.style.transform = `translate(${(side === 'next' ? 1 : -1) * (1 - progress) * 24}px, -50%) scale(${0.8 + progress * 0.2})`
        if (progress >= 1) shown.dataset.armed = ''
        else delete shown.dataset.armed
      } else {
        shown.style.opacity = '0'
      }
    }

    const onClick = (event: MouseEvent) => {
      if (Date.now() < blockClickUntil) {
        event.preventDefault()
        event.stopPropagation()
        blockClickUntil = 0
      }
    }

    box.addEventListener('touchstart', onStart, { passive: true })
    box.addEventListener('touchmove', onMove, { passive: false })
    box.addEventListener('touchend', end)
    box.addEventListener('touchcancel', cancel)
    box.addEventListener('click', onClick, true)
    return () => {
      box.removeEventListener('touchstart', onStart)
      box.removeEventListener('touchmove', onMove)
      box.removeEventListener('touchend', end)
      box.removeEventListener('touchcancel', cancel)
      box.removeEventListener('click', onClick, true)
    }
    // the dialog is there as long as the editor is: bound once per opening
  }, [boxRef, enabled])

  return { slide, reset }
}
